import { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ClipboardCheck, AlertTriangle, Boxes, CalendarClock, Loader2, Wrench, FileDown } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Kpi, Panel, Loading, Empty, Chip, Field, NSelect, useFetch } from "@/components/common";
import { PhotoPicker, Gallery, useUploadsPending } from "@/components/Photos";
import { FileTile } from "@/components/Documents";
import { useUploader } from "@/components/Photos";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fDate, fEur, download } from "@/lib/api";

const ST = { actif: ["Actif", "bg-emerald-50 text-emerald-700 border-emerald-200"], a_surveiller: ["À surveiller", "bg-amber-50 text-amber-700 border-amber-200"],
  maintenance: ["En maintenance", "bg-orange-50 text-orange-700 border-orange-200"], immobilise: ["Immobilisé", "bg-rose-600 text-white border-rose-600"], archive: ["Archivé", "bg-zinc-800 text-white border-zinc-800"] };
const CS = { a_jour: ["À jour", "text-emerald-700"], a_faire: ["À faire", "text-amber-700"], en_retard: ["En retard", "text-rose-700"] };
const COLOR = { vert: "border-emerald-400", orange: "border-amber-400", rouge: "border-rose-500 bg-rose-50" };
const AN = { nouvelle: "Nouvelle", a_surveiller: "À surveiller", a_prevoir: "Intervention à prévoir", programmee: "Intervention programmée", en_cours: "En cours", resolue: "Résolue" };
const Q = {
  etat: ["État général", { bon: "Bon état", a_surveiller: "À surveiller", intervention: "Intervention nécessaire", immobilisation: "Immobilisation nécessaire" }],
  eau: ["Niveau d'eau déminéralisée", { correct: "Niveau correct", complement: "Complément effectué", a_completer: "À compléter", na: "Non applicable" }],
  nettoyage: ["Nettoyage", { complet: "Nettoyage complet effectué", partiel: "Nettoyage partiel", a_refaire: "À refaire" }],
  entretien: ["Entretien nécessaire", { aucun: "Aucun entretien nécessaire", preventif: "Entretien préventif à prévoir", reparation: "Réparation nécessaire", urgente: "Intervention urgente" }],
};
const ACTIONS = ["Ajout d'eau déminéralisée", "Nettoyage", "Graissage", "Petite intervention", "Remplacement d'un élément", "Autre"];
const canMat = (u) => u.role === "chauffeur" || (u.role === "admin" && u.real_role !== "direction");
export const MatStatus = ({ v }) => <Chip cls={ST[v]?.[1]} testid="mat-status-chip">{ST[v]?.[0]}</Chip>;
const Num = ({ n }) => <span className="font-display font-extrabold text-[#0E2D43] tracking-wide">MATÉRIEL N°{n}</span>;

function EqCard({ e, to }) {
  return (
    <Link to={to || `/materiel/fiche/${e.id}`} className={`block bg-white border-2 rounded-xl p-4 hover:shadow-md transition-shadow ${COLOR[e.color]}`} data-testid={`mat-card-${e.number}`}>
      <div className="flex items-center justify-between"><Num n={e.number} /><MatStatus v={e.status} /></div>
      <div className="text-sm font-semibold mt-1">{e.brand} {e.model}</div>
      <div className="text-xs text-slate-500">{e.type}</div>
      <div className="text-xs mt-2">Dernier contrôle : {fDate(e.last_check)} · Prochain : {fDate(e.next_check)} · <b className={CS[e.check_state][1]}>{CS[e.check_state][0]}</b></div>
      {e.next_dekra && <div className="text-xs text-slate-500">DEKRA : {fDate(e.next_dekra)}{e.dekra_level?.stage ? ` (${e.dekra_level.stage})` : ""}</div>}
      {e.open_anomalies > 0 && <div className="text-xs text-rose-700 font-semibold mt-1">{e.open_anomalies} anomalie(s) ouverte(s)</div>}
    </Link>
  );
}

export default function MatDashboard() {
  const { data, loading } = useFetch("/materiel/dashboard");
  const { user } = useAuth();
  if (loading || !data) return <Loading />;
  const k = data.kpis;
  const quick = [["/materiel/controle", "Réaliser un contrôle"], ["/materiel/dashboard#mois", "Voir les contrôles du mois"], ["/materiel/anomalies?new=1", "Signaler une anomalie"], ["/materiel/liste", "Consulter les matériels"], ["/materiel/dekra", "Voir les échéances DEKRA"]].filter(([to]) => user.real_role !== "direction" || !/controle|new=1/.test(to));
  return (
    <div data-testid="mat-dashboard">
      <div className="relative rounded-2xl overflow-hidden mb-6 bg-[#0E2D43]">
        <img src="/materiel-entrepot.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative p-6 sm:p-8 text-white">
          <div className="text-[11px] uppercase tracking-widest text-[#F49619] font-bold">Module 3</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Matériel entrepôt</h1>
          <p className="text-white/80 mt-1">Contrôles mensuels, entretien et suivi des équipements de manutention</p>
          <div className="flex flex-wrap gap-2 mt-4">{quick.map(([to, l]) => <Link key={l} to={to} className="px-3 h-9 inline-flex items-center rounded-full bg-white/10 hover:bg-[#F49619] hover:text-[#0E2D43] text-sm font-semibold transition-colors" data-testid={`mat-quick-${l}`}>{l}</Link>)}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Kpi testid="mat-kpi-total" label="Matériels" value={k.total} sub={`${k.actifs} actifs`} icon={Boxes} tone="navy" />
        <Kpi testid="mat-kpi-watch" label="À surveiller / immobilisés" value={`${k.a_surveiller} / ${k.immobilises}`} icon={AlertTriangle} tone="danger" />
        <Kpi testid="mat-kpi-checks" label="Contrôles du mois à faire" value={k.checks_todo} sub={`dont ${k.checks_late} en retard`} icon={ClipboardCheck} />
        <Kpi testid="mat-kpi-anomalies" label="Anomalies ouvertes" value={k.open_anomalies} icon={Wrench} />
        <Kpi testid="mat-kpi-dekra" label="Prochaine intervention DEKRA" value={k.next_dekra ? fDate(k.next_dekra) : "Non programmée"} sub={`${k.maintenance_due} entretien(s) à prévoir`} icon={CalendarClock} />
      </div>
      <h2 id="mois" className="font-display font-bold text-[#0E2D43] mb-3">Contrôles à faire ce mois-ci</h2>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">{data.equipments.map((e) => <EqCard key={e.id} e={e} />)}</div>
    </div>
  );
}

export function MatControle() {
  const { user } = useAuth();
  const nav = useNavigate();
  const eqs = useFetch("/materiel");
  const uploading = useUploadsPending();
  const [qs] = useSearchParams();
  const [f, setF] = useState({ equipment_id: qs.get("eq") || "", etat: "", eau: "", nettoyage: "", entretien: "", etat_comment: "", nettoyage_comment: "", intervention_detail: "" });
  const [photos, setPhotos] = useState([]);
  const [actions, setActions] = useState([]);
  const [busy, setBusy] = useState(false);
  const nParam = qs.get("n");
  useEffect(() => {
    const e = nParam && eqs.data?.find((x) => String(x.number) === nParam);
    if (e) setF((x) => (x.equipment_id ? x : { ...x, equipment_id: e.id }));
  }, [nParam, eqs.data]);
  if (!eqs.data) return <Loading />;
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const submit = async () => {
    if (["intervention", "immobilisation"].includes(f.etat) && !photos.length) {
      toast.error("Ajoutez au moins une photo : elle est obligatoire pour ce niveau d'état");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/materiel/checks", { ...f, photos: photos.map((p) => p.file_id), actions: actions.map((a) => ({ ...a, cost: a.cost ? Number(a.cost) : null })) });
      toast.success(`Contrôle enregistré${data.anomalies_created ? ` — ${data.anomalies_created} anomalie(s) créée(s)` : ""}${data.new_status === "immobilise" ? " — matériel IMMOBILISÉ" : ""}`);
      nav(`/materiel/fiche/${f.equipment_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const box = "bg-white border border-slate-200 rounded-xl p-4";
  return (
    <div className="max-w-2xl space-y-4 pb-24" data-testid="mat-check-page">
      <PageHeader overline="Matériel entrepôt" title="Contrôle mensuel matériel" />
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="mat-check-picker">
        {eqs.data.map((e) => (
          <button key={e.id} type="button" onClick={() => set("equipment_id")(e.id)} data-testid={`mat-pick-${e.number}`}
            className={`h-16 rounded-lg border-2 font-display font-extrabold ${f.equipment_id === e.id ? "bg-[#0E2D43] text-white border-[#0E2D43]" : "bg-white border-slate-200"}`}>N°{e.number}<div className="text-[10px] font-normal truncate px-1">{e.model}</div></button>
        ))}
      </div>
      <div className={`${box} text-sm`}>Contrôle réalisé par <b data-testid="mat-check-user">{user.name}</b> — date et heure enregistrées automatiquement.</div>
      {Object.entries(Q).map(([k, [label, opts]]) => (
        <div key={k} className={box}>
          <div className="font-semibold text-[#0E2D43] mb-2">{label} *</div>
          <div className="grid sm:grid-cols-2 gap-2">{Object.entries(opts).map(([v, l]) => (
            <button key={v} type="button" onClick={() => set(k)(v)} data-testid={`mat-${k}-${v}`} className={`h-11 rounded-lg border-2 text-sm font-semibold ${f[k] === v ? "bg-[#0E2D43] text-white border-[#0E2D43]" : "bg-white border-slate-200 text-slate-600"}`}>{l}</button>
          ))}</div>
          {k === "etat" && <Textarea rows={2} placeholder="Commentaire" value={f.etat_comment} onChange={set("etat_comment")} className="mt-2 bg-white" data-testid="mat-etat-comment" />}
          {k === "nettoyage" && <Textarea rows={1} placeholder="Commentaire" value={f.nettoyage_comment} onChange={set("nettoyage_comment")} className="mt-2 bg-white" data-testid="mat-nettoyage-comment" />}
          {k === "entretien" && f.entretien && f.entretien !== "aucun" && <Textarea rows={2} placeholder="Détail de l'intervention nécessaire *" value={f.intervention_detail} onChange={set("intervention_detail")} className="mt-2 bg-white" data-testid="mat-intervention-detail" />}
        </div>
      ))}
      <div className={box}>
        <div className="font-semibold text-[#0E2D43] mb-2">Photos {["intervention", "immobilisation"].includes(f.etat) && <span className="text-rose-600">(obligatoire)</span>}</div>
        <PhotoPicker items={photos} onChange={setPhotos} testid="mat-photos" />
      </div>
      <div className={box}>
        <div className="flex justify-between mb-2"><span className="font-semibold text-[#0E2D43]">Actions d'entretien réalisées</span>
          <button type="button" className="text-sm font-bold text-[#E0830B]" onClick={() => setActions([...actions, { kind: ACTIONS[0], description: "", cost: "", comment: "", pending: false }])} data-testid="mat-add-action">+ Ajouter</button></div>
        {actions.map((a, i) => {
          const up = (k, v) => setActions(actions.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
          return (
            <div key={i} className="grid sm:grid-cols-4 gap-2 border-t pt-2 mt-2" data-testid={`mat-action-${i}`}>
              <NSelect value={a.kind} onChange={(v) => up("kind", v)} options={ACTIONS.map((x) => ({ value: x, label: x }))} testid={`mat-action-${i}-kind`} />
              <Input placeholder="Description" value={a.description} onChange={(e) => up("description", e.target.value)} className="bg-white" />
              <Input type="number" placeholder="Coût €" value={a.cost} onChange={(e) => up("cost", e.target.value)} className="bg-white" />
              <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={a.pending} onChange={(e) => up("pending", e.target.checked)} data-testid={`mat-action-${i}-pending`} /> Non réalisée → tâche à traiter</label>
            </div>
          );
        })}
      </div>
      <button onClick={submit} disabled={busy || uploading > 0 || !f.equipment_id} className="w-full h-14 rounded-lg bg-[#F49619] text-[#0E2D43] font-bold disabled:bg-slate-300 sticky bottom-3 shadow-lg" data-testid="mat-check-submit">
        {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : uploading ? "Envoi des photos…" : "Valider le contrôle"}
      </button>
    </div>
  );
}

function EqForm({ e, onDone }) {
  const [f, setF] = useState({ number: "", brand: "", model: "", type: "", serial: "", service_date: "", status: "actif", comment: "", ...e });
  const save = async () => {
    try {
      const body = { ...f, number: Number(f.number), serial: f.serial || null, service_date: f.service_date || null };
      if (e.id) await api.put(`/materiel/${e.id}`, body);
      else await api.post("/materiel", body);
      toast.success("Matériel enregistré");
      onDone();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  return (
    <div className="bg-white border rounded-xl p-4 grid sm:grid-cols-4 gap-3 mb-4" data-testid="mat-form">
      {[["number", "N° interne", "number"], ["brand", "Marque"], ["model", "Modèle"], ["type", "Type"], ["serial", "N° de série"], ["service_date", "Mise en service", "date"]].map(([k, l, t]) => (
        <Field key={k} label={l}><Input type={t || "text"} value={f[k] || ""} onChange={(x) => setF({ ...f, [k]: x.target.value })} className="h-10 bg-white" data-testid={`mat-form-${k}`} /></Field>
      ))}
      <Field label="Statut"><NSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} options={Object.entries(ST).map(([k, x]) => ({ value: k, label: x[0] }))} testid="mat-form-status" /></Field>
      <Field label="Commentaire"><Input value={f.comment} onChange={(x) => setF({ ...f, comment: x.target.value })} className="h-10 bg-white" /></Field>
      <Button onClick={save} className="bg-[#F49619] text-[#0E2D43] font-bold self-end" data-testid="mat-form-save">Enregistrer</Button>
    </div>
  );
}

export function MatList() {
  const { user } = useAuth();
  const [scope, setScope] = useState("actifs");
  const [edit, setEdit] = useState(null);
  const { data, loading, reload } = useFetch("/materiel", { scope });
  return (
    <div data-testid="mat-list-page">
      <PageHeader overline="Matériel entrepôt" title="Matériels">
        <div className="w-36"><NSelect value={scope} onChange={setScope} testid="mat-scope" options={[{ value: "actifs", label: "Actifs" }, { value: "archives", label: "Archivés" }, { value: "tous", label: "Tous" }]} /></div>
        {canMat(user) && <Button onClick={() => setEdit({})} className="bg-[#0E2D43] text-white" data-testid="mat-create-button">Nouveau matériel</Button>}
      </PageHeader>
      {edit && <EqForm e={edit} onDone={() => { setEdit(null); reload(); }} />}
      {loading ? <Loading /> : !data?.length ? <Empty /> : <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">{data.map((e) => <EqCard key={e.id} e={e} />)}</div>}
    </div>
  );
}

function DekraForm({ eid, onDone }) {
  const [f, setF] = useState({ date: "", type: "Contrôle périodique", report: "", observations: "", anomalies: "", actions: "", corrections_date: "", file_ids: [] });
  const up = useUploader((id) => setF((x) => ({ ...x, file_ids: [...x.file_ids, id] })), "document");
  const save = async () => {
    try {
      const { data } = await api.post(`/materiel/${eid}/dekra`, { ...f, corrections_date: f.corrections_date || null });
      toast.success(`Visite enregistrée — prochaine échéance ${fDate(data.next_date)}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="grid sm:grid-cols-3 gap-3 p-4 border-b" data-testid="dekra-form">
      <Field label="Date de l'intervention *"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="dekra-date" /></Field>
      {["type", "report", "observations", "anomalies", "actions"].map((k) => <Field key={k} label={{ type: "Type d'intervention", report: "Compte rendu", observations: "Observations", anomalies: "Anomalies détectées", actions: "Actions à réaliser" }[k]}><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`dekra-${k}`} /></Field>)}
      <Field label="Corrections réalisées le"><Input type="date" value={f.corrections_date} onChange={(e) => setF({ ...f, corrections_date: e.target.value })} /></Field>
      <Field label="Rapport PDF"><input type="file" accept="application/pdf,image/*" onChange={(e) => { up.handle(e.target.files); e.target.value = ""; }} data-testid="dekra-file-input" /></Field>
      <Button onClick={save} disabled={!f.date || up.busy > 0} className="bg-[#F49619] text-[#0E2D43] font-bold self-end" data-testid="dekra-save">Enregistrer la visite (+6 mois)</Button>
    </div>
  );
}

export function MatFiche() {
  const { id } = useParams();
  const { user } = useAuth();
  const admin = canMat(user);
  const { data, loading, reload } = useFetch(`/materiel/${id}`);
  const [tab, setTab] = useState("checks");
  const [edit, setEdit] = useState(false);
  const [doc, setDoc] = useState({ doc_type: "rapport_dekra", file_ids: [], comment: "" });
  const up = useUploader((fid) => setDoc((x) => ({ ...x, file_ids: [...x.file_ids, fid] })), "document");
  if (loading || !data) return <Loading />;
  const e = data.equipment;
  const saveDoc = async () => {
    try {
      await api.post(`/materiel/${id}/documents`, doc);
      setDoc({ doc_type: "rapport_dekra", file_ids: [], comment: "" });
      reload();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  const tabs = [["checks", `Contrôles (${data.checks.length})`], ["anomalies", `Anomalies (${data.anomalies.length})`], ["dekra", "Entretien DEKRA"], ["docs", `Documents (${data.documents.length})`], ["events", "Historique statut"]];
  return (
    <div data-testid="mat-fiche">
      <PageHeader overline={`${e.brand} ${e.model} · ${e.type}`} title={<Num n={e.number} />}>
        <MatStatus v={e.status} />{admin && <Button variant="outline" onClick={() => setEdit(!edit)} data-testid="mat-edit-button">Modifier</Button>}
      </PageHeader>
      {edit && <EqForm e={e} onDone={() => { setEdit(false); reload(); }} />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 text-sm">
        {[["Dernier contrôle", fDate(e.last_check)], ["Prochain contrôle", `${fDate(e.next_check)} · ${CS[e.check_state][0]}`], ["Dernier DEKRA", fDate(e.last_dekra)], ["Prochain DEKRA", fDate(e.next_dekra)],
          ["N° de série", e.serial || "—"], ["Mise en service", fDate(e.service_date)], ["Commentaire", e.comment || "—"]].map(([l, v]) => <div key={l} className="bg-white border rounded-lg p-3"><div className="text-[11px] uppercase text-slate-400 font-bold">{l}</div><div className="font-semibold" data-testid={`mat-info-${l}`}>{v}</div></div>)}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">{tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3 h-8 rounded-full text-xs font-semibold border ${tab === k ? "bg-[#0E2D43] text-white" : "bg-white"}`} data-testid={`mat-tab-${k}`}>{l}</button>)}</div>
      <Panel flush>
        {tab === "checks" && (!data.checks.length ? <Empty text="Aucun contrôle" /> : data.checks.map((c) => (
          <div key={c.id} className="p-4 border-b text-sm" data-testid={`mat-check-${c.id}`}>
            <b>{new Date(c.created_at).toLocaleString("fr-FR")}</b> — {c.by}
            <div className="text-slate-600">{Object.entries(Q).map(([k, [l, o]]) => `${l} : ${o[c[k]]}`).join(" · ")}</div>
            {[c.etat_comment, c.nettoyage_comment, c.intervention_detail].filter(Boolean).map((x) => <div key={x} className="text-slate-500">{x}</div>)}
            {c.actions.map((a, i) => <div key={i} className="text-xs">Action : {a.kind} {a.description} {a.cost ? `· ${fEur(a.cost)}` : ""} {a.pending ? "(non réalisée)" : ""}</div>)}
            <div className="mt-2"><Gallery ids={c.photos} testid={`mat-check-photos-${c.id}`} /></div>
          </div>
        )))}
        {tab === "anomalies" && <AnomalyList items={data.anomalies} admin={admin} onDone={reload} />}
        {tab === "dekra" && (<>
          {admin && <DekraForm eid={id} onDone={reload} />}
          {!data.dekra.length ? <Empty text="Aucune intervention DEKRA enregistrée" /> : data.dekra.map((d) => (
            <div key={d.id} className="p-4 border-b text-sm"><b>{fDate(d.date)}</b> — DEKRA · {d.type} · prochaine : {fDate(d.next_date)}
              <div className="text-slate-500">{[d.report, d.observations, d.anomalies && `Anomalies : ${d.anomalies}`, d.actions && `Actions : ${d.actions}`, d.corrections_date && `Corrigé le ${fDate(d.corrections_date)}`].filter(Boolean).join(" · ")}</div>
              <div className="flex gap-2 mt-2">{d.file_ids.map((f) => <FileTile key={f} id={f} />)}</div></div>
          ))}
        </>)}
        {tab === "docs" && (<>
          {admin && (
            <div className="p-4 border-b flex flex-wrap gap-2 items-end" data-testid="mat-doc-form">
              <div className="w-48"><NSelect value={doc.doc_type} onChange={(v) => setDoc({ ...doc, doc_type: v })} options={Object.entries(data.doc_types).map(([k, l]) => ({ value: k, label: l }))} testid="mat-doc-type" /></div>
              <input type="file" accept="application/pdf,image/*" onChange={(ev) => { up.handle(ev.target.files); ev.target.value = ""; }} data-testid="mat-doc-file-input" />
              <Input placeholder="Commentaire" value={doc.comment} onChange={(ev) => setDoc({ ...doc, comment: ev.target.value })} className="w-48" />
              <Button onClick={saveDoc} disabled={!doc.file_ids.length || up.busy > 0} data-testid="mat-doc-save">Ajouter ({doc.file_ids.length})</Button>
            </div>
          )}
          {!data.documents.length ? <Empty text="Aucun document" /> : data.documents.map((d) => (
            <div key={d.id} className="p-4 border-b flex gap-3 text-sm"><div className="flex gap-2">{d.file_ids.map((f) => <FileTile key={f} id={f} />)}</div><div><b>{d.type_label}</b><div className="text-slate-500">{fDate(d.created_at.slice(0, 10))} · {d.added_by} {d.comment && `· ${d.comment}`}</div></div></div>
          ))}
        </>)}
        {tab === "events" && (!data.events.length ? <Empty text="Aucun changement de statut" /> : data.events.map((x) => <div key={x.id} className="p-3 border-b text-sm">{new Date(x.date).toLocaleString("fr-FR")} — {x.label} ({x.by})</div>))}
      </Panel>
    </div>
  );
}

function AnomalyList({ items, admin, onDone }) {
  const change = async (a, status) => {
    try {
      await api.patch(`/materiel/anomalies/${a.id}`, { status });
      toast.success(`Statut mis à jour : ${AN[status]}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!items.length) return <Empty text="Aucune anomalie" />;
  return items.map((a) => (
    <div key={a.id} className={`p-4 border-b text-sm ${a.severity === "rouge" && a.status !== "resolue" ? "bg-rose-50" : ""}`} data-testid={`mat-anomaly-${a.id}`}>
      <div className="flex flex-wrap items-center gap-2">{a.number && <Num n={a.number} />}<b>{a.nature}</b><span className="text-slate-500">{fDate(a.date)} · {a.by}</span>
        {admin ? <div className="w-52 ml-auto"><NSelect value={a.status} onChange={(v) => change(a, v)} options={Object.entries(AN).map(([k, l]) => ({ value: k, label: l }))} testid={`mat-anomaly-status-${a.id}`} /></div> : <Chip cls="ml-auto">{AN[a.status]}</Chip>}</div>
      {a.description && <div className="text-slate-600">{a.description}</div>}
      <div className="mt-2"><Gallery ids={a.photos || []} testid={`mat-anomaly-photos-${a.id}`} /></div>
    </div>
  ));
}

export function MatAnomalies() {
  const { user } = useAuth();
  const [status, setStatus] = useState("ouvertes");
  const { data, reload } = useFetch("/materiel/anomalies", { status });
  const eqs = useFetch("/materiel");
  const [f, setF] = useState({ equipment_id: "", nature: "", description: "" });
  const [photos, setPhotos] = useState([]);
  const send = async () => {
    try {
      await api.post("/materiel/anomalies", { ...f, photos: photos.map((p) => p.file_id) });
      toast.success("Anomalie signalée");
      setF({ equipment_id: "", nature: "", description: "" });
      setPhotos([]);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="mat-anomalies-page">
      <PageHeader overline="Matériel entrepôt" title="Anomalies"><div className="w-44"><NSelect value={status} onChange={setStatus} testid="mat-anomaly-filter" options={[{ value: "ouvertes", label: "Ouvertes" }, ...Object.entries(AN).map(([k, l]) => ({ value: k, label: l }))]} /></div></PageHeader>
      {user.real_role !== "direction" && <Panel title="Signaler une anomalie" className="mb-6" testid="mat-anomaly-form">
        <div className="grid sm:grid-cols-3 gap-3">
          <NSelect value={f.equipment_id} onChange={(v) => setF({ ...f, equipment_id: v })} placeholder="Matériel…" options={(eqs.data || []).map((e) => ({ value: e.id, label: `N°${e.number} — ${e.model}` }))} testid="mat-anomaly-equipment" />
          <Input placeholder="Nature *" value={f.nature} onChange={(e) => setF({ ...f, nature: e.target.value })} data-testid="mat-anomaly-nature" />
          <Input placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="mat-anomaly-description" />
        </div>
        <div className="mt-3"><PhotoPicker items={photos} onChange={setPhotos} testid="mat-anomaly-photos" /></div>
        <Button onClick={send} disabled={!f.equipment_id || !f.nature} className="mt-3 bg-[#F49619] text-[#0E2D43] font-bold" data-testid="mat-anomaly-submit">Signaler</Button>
      </Panel>}
      <Panel flush>{data ? <AnomalyList items={data} admin={canMat(user)} onDone={reload} /> : <Loading />}</Panel>
    </div>
  );
}

export function MatDekra() {
  const { user } = useAuth();
  const { data, reload } = useFetch("/materiel");
  const [sel, setSel] = useState([]);
  const [date, setDate] = useState("2026-10-28");
  if (!data) return <Loading />;
  const schedule = async () => {
    try {
      const { data: r } = await api.post("/materiel/dekra/schedule", { equipment_ids: sel, date });
      toast.success(`${r.scheduled} matériel(s) programmé(s) au ${fDate(date)}`);
      setSel([]);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="mat-dekra-page">
      <PageHeader overline="Matériel entrepôt" title="Entretien / DEKRA" />
      {canMat(user) && (
        <Panel title="Programmer un rendez-vous DEKRA" className="mb-6" testid="dekra-schedule">
          <div className="flex flex-wrap gap-2 mb-3">{data.map((e) => <label key={e.id} className={`px-3 h-9 inline-flex items-center gap-1 rounded-full border text-sm cursor-pointer ${sel.includes(e.id) ? "bg-[#0E2D43] text-white" : "bg-white"}`} data-testid={`dekra-select-${e.number}`}>
            <input type="checkbox" className="hidden" checked={sel.includes(e.id)} onChange={() => setSel(sel.includes(e.id) ? sel.filter((x) => x !== e.id) : [...sel, e.id])} />N°{e.number}</label>)}</div>
          <div className="flex gap-2"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="dekra-schedule-date" /><Button onClick={schedule} disabled={!sel.length} data-testid="dekra-schedule-save">Programmer</Button></div>
          <p className="text-xs text-slate-500 mt-2">Après la visite, enregistrez-la depuis la fiche du matériel (onglet Entretien DEKRA) : la prochaine échéance est calculée à +6 mois.</p>
        </Panel>
      )}
      {canMat(user) && <BulkDekra eqs={data} onDone={reload} />}
      <div className="bg-white border rounded-xl overflow-x-auto"><table className="tbl w-full" data-testid="dekra-table">
        <thead><tr><th>Matériel</th><th>Dernière intervention</th><th>Prochaine intervention</th><th>Alerte</th></tr></thead>
        <tbody>{data.map((e) => <tr key={e.id}><td><Link to={`/materiel/fiche/${e.id}`} className="hover:underline"><Num n={e.number} /></Link> <span className="text-xs text-slate-500">{e.model}</span></td><td>{fDate(e.last_dekra)}</td><td>{e.next_dekra ? fDate(e.next_dekra) : "Non programmée"}</td>
          <td>{e.dekra_level ? <Chip cls={e.dekra_level.level === "expire" ? "bg-rose-50 text-rose-700 border-rose-200" : e.dekra_level.level === "bientot" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>{e.dekra_level.stage || "OK"}</Chip> : "—"}</td></tr>)}</tbody>
      </table></div>
    </div>
  );
}

function BulkDekra({ eqs, onDone }) {
  const [sel, setSel] = useState(eqs.map((e) => e.id));
  const [f, setF] = useState({ date: "2026-10-28", type: "Contrôle périodique", report: "", observations: "", file_ids: [] });
  const [busy, setBusy] = useState(false);
  const up = useUploader((id) => setF((x) => ({ ...x, file_ids: [...x.file_ids, id] })), "document");
  const save = async () => {
    if (!window.confirm(`Enregistrer la visite DEKRA du ${fDate(f.date)} sur ${sel.length} matériel(s) ?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/materiel/dekra/bulk", { ...f, equipment_ids: sel });
      toast.success(`Visite enregistrée sur ${data.recorded} matériel(s) — prochaine échéance ${fDate(data.next_date)}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const all = sel.length === eqs.length;
  return (
    <Panel title="Enregistrer une visite DEKRA groupée" className="mb-6" testid="dekra-bulk">
      <div className="flex flex-wrap gap-2 mb-3">
        <button type="button" onClick={() => setSel(all ? [] : eqs.map((e) => e.id))} className="px-3 h-9 rounded-full border text-sm font-semibold text-[#E0830B]" data-testid="dekra-bulk-all">{all ? "Tout désélectionner" : "Tout sélectionner"}</button>
        {eqs.map((e) => (
          <button key={e.id} type="button" onClick={() => setSel(sel.includes(e.id) ? sel.filter((x) => x !== e.id) : [...sel, e.id])} data-testid={`dekra-bulk-${e.number}`}
            className={`px-3 h-9 rounded-full border text-sm ${sel.includes(e.id) ? "bg-[#0E2D43] text-white" : "bg-white"}`}>N°{e.number}</button>
        ))}
      </div>
      <div className="grid sm:grid-cols-4 gap-3">
        <Field label="Date de la visite *"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="dekra-bulk-date" /></Field>
        <Field label="Type d'intervention"><Input value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="dekra-bulk-type" /></Field>
        <Field label="Compte rendu"><Input value={f.report} onChange={(e) => setF({ ...f, report: e.target.value })} data-testid="dekra-bulk-report" /></Field>
        <Field label="Observations"><Input value={f.observations} onChange={(e) => setF({ ...f, observations: e.target.value })} data-testid="dekra-bulk-observations" /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <input type="file" accept="application/pdf,image/*" onChange={(e) => { up.handle(e.target.files); e.target.value = ""; }} data-testid="dekra-bulk-file-input" />
        <span className="text-xs text-slate-500">{f.file_ids.length} rapport(s) joint(s)</span>
        <Button variant="outline" disabled={!f.date} onClick={() => download("/materiel/dekra/recap", { date: f.date }, `recap-dekra-${f.date}.pdf`).catch(() => toast.error("Aucune visite DEKRA enregistrée ou programmée à cette date"))} className="ml-auto" data-testid="dekra-recap-pdf">
          <FileDown className="w-4 h-4 mr-1" /> Récapitulatif PDF
        </Button>
        <Button onClick={save} disabled={busy || !sel.length || !f.date || up.busy > 0} className="bg-[#F49619] text-[#0E2D43] font-bold" data-testid="dekra-bulk-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Enregistrer la visite sur ${sel.length} matériel(s)`}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mt-2">Chaque matériel reçoit sa propre fiche de visite ; la prochaine échéance est fixée à +6 mois.</p>
    </Panel>
  );
}

export function MatQR() {
  const { data } = useFetch("/materiel");
  if (!data) return <Loading />;
  const base = window.location.origin;
  return (
    <div data-testid="mat-qr-page">
      <PageHeader overline="Matériel entrepôt" title="QR codes matériels">
        <Button onClick={() => window.print()} className="bg-[#F49619] text-[#0E2D43] font-bold" data-testid="mat-qr-print">Imprimer les étiquettes</Button>
      </PageHeader>
      <p className="text-sm text-slate-500 mb-4 print:hidden">Imprimez puis collez chaque étiquette sur son matériel. En la scannant avec l'appareil photo du téléphone, le contrôle mensuel s'ouvre avec le matériel déjà sélectionné (après connexion si nécessaire).</p>
      <div className="print-area grid grid-cols-2 sm:grid-cols-3 gap-4">
        {data.map((e) => (
          <div key={e.id} className="bg-white border-2 border-[#0E2D43] rounded-xl p-4 flex flex-col items-center text-center break-inside-avoid" data-testid={`mat-qr-${e.number}`}>
            <img src="/logo-presthyg-bleu.png" alt="Prest'hyg" className="h-12 mb-2" />
            <QRCodeSVG value={`${base}/materiel/controle?n=${e.number}`} size={150} fgColor="#0E2D43" level="M" />
            <div className="mt-2"><Num n={e.number} /></div>
            <div className="text-xs text-slate-600">{e.model === e.brand ? e.brand : `${e.brand} ${e.model}`}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#E0830B] font-bold mt-1">Scanner pour le contrôle mensuel</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MatDocuments() {
  const { data } = useFetch("/materiel/documents/all");
  if (!data) return <Loading />;
  return (
    <div data-testid="mat-documents-page">
      <PageHeader overline="Matériel entrepôt" title="Documents" />
      <Panel flush>{!data.length ? <Empty text="Aucun document — ajoutez-les depuis la fiche de chaque matériel" /> : data.map((d) => (
        <div key={d.id} className="p-4 border-b flex gap-3 text-sm"><div className="flex gap-2">{d.file_ids.map((f) => <FileTile key={f} id={f} />)}</div>
          <div><Link to={`/materiel/fiche/${d.equipment_id}`}><Num n={d.number} /></Link><div><b>{d.type_label}</b> · {fDate(d.created_at.slice(0, 10))} · {d.added_by}</div></div></div>
      ))}</Panel>
    </div>
  );
}
